import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
  useGetExtendEstimates,
  useGetDirectLaborCloseouts,
  useGetLaborFactoringCloseouts,
  useGetIncidentalLaborCloseouts,
  useGetIndirectLaborCloseouts,
  useGetEquipmentCloseouts,
  useGetGeneralExpenseCloseouts,
  useGetSubcontractCloseouts,
  useGetQuoteCloseouts,
  useGetTotalFinalPriceCloseouts,
  useGetExtendChangeOrder,
  useGetDirectLaborChangeOrderCloseouts,
  useGetEquipmentChangeOrderCloseouts,
  useGetGeneralExpenseChangeOrderCloseouts,
  useGetIncidentalLaborChangeOrderCloseouts,
  useGetIndirectLaborChangeOrderCloseouts,
  useGetLaborFactoringChangeOrderCloseouts,
  useGetQuoteChangeOrderCloseouts,
  useGetSubcontractChangeOrderCloseouts,
  useGetTotalFinalPriceChangeOrderCloseouts,
} from './useCloseoutHooks';

// Define the interface for the result of a hook
export interface CloseoutHookResult {
  data: any;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export const useCloseoutQueriesStore = (isEstimate: boolean, estimateId?: string) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  // Initialize all hooks at the component level to follow React Hooks rules
  const extendEstimates = useGetExtendEstimates();
  const directLabor = useGetDirectLaborCloseouts();
  const laborFactoring = useGetLaborFactoringCloseouts();
  const incidentalLabor = useGetIncidentalLaborCloseouts();
  const indirectLabor = useGetIndirectLaborCloseouts();
  const equipment = useGetEquipmentCloseouts();
  const generalExpense = useGetGeneralExpenseCloseouts();
  const subcontract = useGetSubcontractCloseouts();
  const quoteEstimate = useGetQuoteCloseouts();
  const totalPricing = useGetTotalFinalPriceCloseouts();

  const extendChangeOrder = useGetExtendChangeOrder();
  const directLaborChangeOrder = useGetDirectLaborChangeOrderCloseouts();
  const laborFactoringChangeOrder = useGetLaborFactoringChangeOrderCloseouts();
  const incidentalLaborChangeOrder = useGetIncidentalLaborChangeOrderCloseouts();
  const indirectLaborChangeOrder = useGetIndirectLaborChangeOrderCloseouts();
  const equipmentChangeOrder = useGetEquipmentChangeOrderCloseouts();
  const generalExpenseChangeOrder = useGetGeneralExpenseChangeOrderCloseouts();
  const subcontractChangeOrder = useGetSubcontractChangeOrderCloseouts();
  const quoteChangeOrder = useGetQuoteChangeOrderCloseouts();
  const totalPricingChangeOrder = useGetTotalFinalPriceChangeOrderCloseouts();

  // Hook mappings organized by tab index - reference the initialized hooks
  const estimateHooks = useMemo(
    () => [
      extendEstimates,
      directLabor,
      laborFactoring,
      incidentalLabor,
      indirectLabor,
      equipment,
      generalExpense,
      subcontract,
      quoteEstimate,
      totalPricing,
      { refetch: totalPricing.refetch }, // Schedule of Values uses totalPricing's refetch
    ],
    [
      extendEstimates,
      directLabor,
      laborFactoring,
      incidentalLabor,
      indirectLabor,
      equipment,
      generalExpense,
      subcontract,
      quoteEstimate,
      totalPricing,
    ]
  );

  const changeOrderHooks = useMemo(
    () => [
      extendChangeOrder,
      directLaborChangeOrder,
      laborFactoringChangeOrder,
      incidentalLaborChangeOrder,
      indirectLaborChangeOrder,
      equipmentChangeOrder,
      generalExpenseChangeOrder,
      subcontractChangeOrder,
      quoteChangeOrder,
      totalPricingChangeOrder,
      { refetch: totalPricingChangeOrder.refetch }, // Schedule of Values uses totalPricingChangeOrder's refetch
    ],
    [
      extendChangeOrder,
      directLaborChangeOrder,
      laborFactoringChangeOrder,
      incidentalLaborChangeOrder,
      indirectLaborChangeOrder,
      equipmentChangeOrder,
      generalExpenseChangeOrder,
      subcontractChangeOrder,
      quoteChangeOrder,
      totalPricingChangeOrder,
    ]
  );

  // Key mappings for prefetching
  const getQueryKeys = useCallback(
    (tabIndex: number, id?: string) => {
      const baseKeys = [
        ['extendEstimates', 'extendChangeOrder'],
        ['directLabor', 'directLaborChangeOrder'],
        ['laborFactoring', 'laborFactoringChangeOrder'],
        ['incidentalLabor', 'incidentalLaborChangeOrder'],
        ['indirectLabor', 'indirectLaborChangeOrder'],
        ['equipment', 'equipmentChangeOrder'],
        ['generalExpense', 'generalExpenseChangeOrder'],
        ['subcontract', 'subcontractChangeOrder'],
        ['quote', 'quoteChangeOrder'],
        ['totalPricing', 'totalPricingChangeOrder'],
        ['scheduleOfValues', 'scheduleOfValues'],
      ];

      if (tabIndex >= 0 && tabIndex < baseKeys.length) {
        const keyIndex = isEstimate ? 0 : 1;
        return id ? [baseKeys[tabIndex][keyIndex], id] : [baseKeys[tabIndex][keyIndex]];
      }
      return [];
    },
    [isEstimate]
  );

  // Get the appropriate hook for the current tab
  const getActiveHook = useCallback(() => {
    const hooks = isEstimate ? estimateHooks : changeOrderHooks;
    if (activeTab >= 0 && activeTab < hooks.length) {
      return hooks[activeTab];
    }
    return null;
  }, [activeTab, isEstimate, estimateHooks, changeOrderHooks]);

  // Prefetch data for a specific tab
  const prefetchTabData = useCallback(
    (tabIndex: number) => {
      if (tabIndex === activeTab) return; // Don't prefetch current tab

      const queryKey = getQueryKeys(tabIndex, estimateId);
      if (queryKey.length) {
        // We're just prefetching so we don't need the actual fetcher function
        // The key is enough as React Query will use the registered fetcher
        queryClient.prefetchQuery(queryKey);
      }
    },
    [activeTab, estimateId, queryClient, getQueryKeys]
  );

  return {
    activeTab,
    setActiveTab,
    getActiveHook,
    prefetchTabData,
    // Also expose all hooks for direct access if needed
    hooks: {
      isEstimate: {
        extendEstimates,
        directLabor,
        laborFactoring,
        incidentalLabor,
        indirectLabor,
        equipment,
        generalExpense,
        subcontract,
        quoteEstimate,
        totalPricing,
      },
      changeOrder: {
        extendChangeOrder,
        directLaborChangeOrder,
        laborFactoringChangeOrder,
        incidentalLaborChangeOrder,
        indirectLaborChangeOrder,
        equipmentChangeOrder,
        generalExpenseChangeOrder,
        subcontractChangeOrder,
        quoteChangeOrder,
        totalPricingChangeOrder,
      },
    },
  };
};
